/* eslint-disable react/prop-types */
import { Link } from "react-router-dom";
import { MotionConfig } from "framer-motion";
import {
    ArrowRight, PencilRuler, Boxes, Eye, Save, ClipboardCheck, FileCheck2,
    Store, Wrench, Mail, ShieldCheck, Layers,
} from "lucide-react";
import "./HomePage.css";
import { FadeIn, SlideCard, StaggerList, BrandButton } from "../../components/ui/index.js";
import CatalogPreview from "./components/CatalogPreview.jsx";
import HomeModel from "./components/HomeModel.jsx";

// ─── Contenido (solo afirmaciones verificables — ver landing-claims.md) ───────

// Cómo funciona: el recorrido real que una persona puede hacer hoy.
const howItWorks = [
    { icon: <PencilRuler size={18} />,   title: "Diseña en el navegador", desc: "Edita texto, formas y colores y ajusta la forma base. Sin instalar nada." },
    { icon: <Eye size={18} />,           title: "Previsualiza en 3D",     desc: "Comprueba cómo queda tu diseño con la vista 3D antes de continuar." },
    { icon: <Save size={18} />,          title: "Guárdalo en tu cuenta",  desc: "Tu diseño queda guardado para retomarlo o enviarlo cuando quieras." },
    { icon: <ClipboardCheck size={18} />,title: "Envía a revisión",       desc: "Solicita la fabricación y una persona revisa tu diseño." },
    { icon: <FileCheck2 size={18} />,    title: "Recibe tu propuesta",    desc: "Te preparamos un precio revisado por una persona, no automático." },
];

// El configurador: capacidad del editor, sin prometer catálogo físico garantizado.
const editorFeatures = [
    { icon: <PencilRuler size={16} />, text: "Piezas planas: diseña sobre un lienzo con texto, formas y colores." },
    { icon: <Boxes size={16} />,       text: "Boxes o cajas: configura una caja personalizada, incluida la de luz." },
    { icon: <Eye size={16} />,         text: "Previsualización 3D de lo que estás creando." },
];

// Confianza basada en proceso verificable, sin superlativos.
const trustPoints = [
    { icon: <Save size={20} />,        title: "Tu diseño se guarda",        desc: "Cada diseño queda asociado a tu cuenta para retomarlo cuando quieras." },
    { icon: <ShieldCheck size={20} />, title: "Revisión por una persona",   desc: "Antes de dar un precio, alguien comprueba tu diseño. Nada es automático." },
    { icon: <Layers size={20} />,      title: "Propuesta por enlace privado", desc: "Cuando se aprueba, la propuesta se añade al carrito desde un enlace propio." },
];

const paths = [
    { to: "/customizers", icon: <Wrench size={28} />, title: "Crear un diseño", desc: "Abre el configurador y empieza tu pieza. Necesitas una cuenta.", btn: "Empezar a diseñar" },
    { to: "/store",       icon: <Store size={28} />,  title: "Ver la tienda",   desc: "Explora los productos de stock disponibles.", btn: "Ir a la tienda" },
    { to: "/contact",     icon: <Mail size={28} />,   title: "Contar tu idea",  desc: "¿Tienes un proyecto en mente? Escríbenos.", btn: "Contactar" },
];

// Clases compartidas de las tarjetas de sección, para no repetirlas.
const CARD = "glassCard rounded-2xl shadow-xl w-full md:w-[52%] lg:w-[45%] max-h-[88vh] overflow-y-auto p-5 md:p-8 flex flex-col gap-4";
const SECTION_LEFT = "flex items-center justify-center md:justify-start w-full h-full overflow-hidden";
const SECTION_RIGHT = "flex items-center justify-center md:justify-end w-full h-full overflow-hidden";
const PADDING = "flex items-center w-full h-full p-3 md:p-8 lg:p-12";

function Eyebrow({ children, tone = "primary" }) {
    const color = tone === "secondary" ? "text-secondary" : "text-primary";
    return (
        <p className={`text-xs md:text-sm uppercase tracking-widest ${color} font-semibold mb-1`}>
            {children}
        </p>
    );
}

// ─── HOME ────────────────────────────────────────────────────────────────────
function HomePage() {
    return (
        // reducedMotion="user" hace que FadeIn/SlideCard/StaggerList respeten
        // prefers-reduced-motion. La animación 3D del logo se conserva a propósito.
        <MotionConfig reducedMotion="user">
            <main
                id="wrapper_background"
                aria-label="Inicio de Customiz3D"
                className="text-gray-900 dark:text-gray-200 h-full w-full overflow-y-scroll overflow-x-hidden wrapper_background"
                style={{ scrollSnapType: "y mandatory" }}
            >
                {/* ── 0 · Reveal del logo 3D (animación) ─────────────────────── */}
                <section id="section-0" className="logoCenter" aria-label="Customiz3D">
                    <div className="container" />
                    {/* Pista de propuesta, sin tapar el logo centrado. */}
                    <FadeIn className="pointer-events-none absolute inset-x-0 bottom-10 md:bottom-14 flex flex-col items-center gap-1 px-4 text-center">
                        <p className="text-sm md:text-base font-medium text-gray-700 dark:text-gray-300">
                            Diseña · Previsualiza en 3D · Envía a revisión
                        </p>
                        <p className="text-xs uppercase tracking-widest text-primary font-semibold">
                            Desliza para empezar
                        </p>
                    </FadeIn>
                </section>

                {/* ── 1 · Hero / propuesta (tarjeta izquierda) ───────────────── */}
                <section id="section-1" className={SECTION_LEFT}>
                    <div className={`${PADDING} justify-center md:justify-start`}>
                        <SlideCard direction="left" className={CARD}>
                            <div>
                                <Eyebrow>Diseño 3D personalizado</Eyebrow>
                                <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold leading-tight dark:text-white text-gray-900">
                                    Diseña objetos personalizados en 3D desde el navegador
                                </h1>
                            </div>
                            <p className="text-base md:text-lg text-gray-700 dark:text-gray-300">
                                Crea tu diseño, visualízalo en 3D y envíalo para que revisemos su
                                fabricación y te preparemos un precio. Todo desde el navegador,
                                sin instalar programas.
                            </p>
                            <div className="flex flex-wrap gap-3 pt-2">
                                <BrandButton as="link" to="/customizers" size="sm">
                                    Empezar a diseñar <ArrowRight size={14} />
                                </BrandButton>
                                <BrandButton as="link" to="/store" size="sm" variant="outline">
                                    Ver la tienda
                                </BrandButton>
                            </div>
                        </SlideCard>
                    </div>
                </section>

                {/* ── 2 · Catálogo real (pantalla completa, logo alejado) ────── */}
                <section
                    id="section-2"
                    className="flex flex-col items-center justify-center w-full h-full overflow-hidden"
                    aria-label="Tienda"
                >
                    <div className="w-full h-full flex flex-col items-center justify-center px-4 md:px-12 py-4 gap-4 md:gap-6">
                        <FadeIn className="text-center">
                            <Eyebrow>Tienda</Eyebrow>
                            <h2 className="text-2xl md:text-4xl font-extrabold dark:text-white text-gray-900">
                                Productos de stock disponibles
                            </h2>
                        </FadeIn>

                        <CatalogPreview limit={4} />

                        <FadeIn>
                            <BrandButton as="link" to="/store" size="md">
                                Explorar la tienda <ArrowRight size={16} />
                            </BrandButton>
                        </FadeIn>
                    </div>
                </section>

                {/* ── 3 · El configurador (tarjeta derecha) ──────────────────── */}
                <section id="section-3" className={SECTION_RIGHT}>
                    <div className={`${PADDING} justify-center md:justify-end`}>
                        <SlideCard direction="right" className={CARD}>
                            <div>
                                <Eyebrow tone="secondary">El configurador</Eyebrow>
                                <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold leading-tight dark:text-white text-gray-900">
                                    Dos herramientas para diseñar
                                </h2>
                            </div>
                            <p className="text-base md:text-lg text-gray-700 dark:text-gray-300">
                                Diseña en el navegador con nuestro editor y previsualiza el
                                resultado en 3D antes de enviarlo a revisión.
                            </p>

                            {/* Capturas reales de la herramienta (editor 2D y vista
                                3D) + el resultado explorable en 3D. */}
                            <figure className="grid grid-cols-2 gap-2 md:gap-3 m-0">
                                {[
                                    { src: "/home/editor-2d.webp", label: "Editor 2D" },
                                    { src: "/home/editor-3d.webp", label: "Vista 3D" },
                                ].map((shot) => (
                                    <div
                                        key={shot.src}
                                        className="group/shot rounded-xl overflow-hidden border border-black/10 dark:border-white/10 shadow-lg bg-base-300"
                                    >
                                        <img
                                            src={shot.src}
                                            alt={`Customiz3r — ${shot.label}`}
                                            loading="lazy"
                                            width="1000"
                                            height="625"
                                            className="w-full aspect-[16/10] object-cover object-top
                                                transition-transform duration-500 ease-out
                                                group-hover/shot:scale-[1.04] motion-reduce:transition-none"
                                        />
                                        <figcaption className="text-[11px] md:text-xs text-center py-1 px-2
                                            bg-base-300/80 text-gray-600 dark:text-gray-300 truncate">
                                            {shot.label}
                                        </figcaption>
                                    </div>
                                ))}
                            </figure>

                            <StaggerList
                                items={editorFeatures}
                                className="flex flex-col gap-3"
                                renderItem={(f) => (
                                    <div className="flex items-start gap-3 text-sm md:text-base">
                                        <span className="text-secondary mt-0.5 flex-shrink-0">{f.icon}</span>
                                        <span>{f.text}</span>
                                    </div>
                                )}
                            />
                            <div className="flex flex-wrap gap-3 pt-2">
                                <BrandButton as="link" to="/customizers" size="sm">
                                    Empezar a diseñar <ArrowRight size={14} />
                                </BrandButton>
                            </div>
                        </SlideCard>
                    </div>
                </section>

                {/* ── 4 · Cómo funciona (tarjeta izquierda) ──────────────────── */}
                <section id="section-4" className={SECTION_LEFT}>
                    <div className={`${PADDING} justify-center md:justify-start`}>
                        <SlideCard direction="left" className={CARD}>
                            <div>
                                <Eyebrow>Cómo funciona</Eyebrow>
                                <h2 className="text-3xl md:text-4xl font-extrabold dark:text-white text-gray-900">
                                    De la idea a una propuesta
                                </h2>
                            </div>
                            <StaggerList
                                items={howItWorks}
                                className="flex flex-col gap-3"
                                renderItem={(s, i) => (
                                    <div className="flex items-start gap-3">
                                        <span className="mt-0.5 text-primary flex-shrink-0" aria-hidden>{s.icon}</span>
                                        <div>
                                            <p className="font-bold text-sm md:text-base dark:text-white text-gray-900">
                                                <span className="text-primary/50 mr-1">{String(i + 1).padStart(2, "0")}</span>
                                                {s.title}
                                            </p>
                                            <p className="text-sm text-gray-600 dark:text-gray-400">{s.desc}</p>
                                        </div>
                                    </div>
                                )}
                            />
                        </SlideCard>
                    </div>
                </section>

                {/* ── 5 · Confianza por proceso (tarjeta derecha) ────────────── */}
                <section id="section-5" className={SECTION_RIGHT}>
                    <div className={`${PADDING} justify-center md:justify-end`}>
                        <SlideCard direction="right" className={CARD}>
                            <div>
                                <Eyebrow tone="secondary">Cómo trabajamos</Eyebrow>
                                <h2 className="text-3xl md:text-4xl font-extrabold dark:text-white text-gray-900">
                                    Revisado por una persona
                                </h2>
                            </div>
                            <StaggerList
                                items={trustPoints}
                                className="flex flex-col gap-4"
                                renderItem={(t) => (
                                    <div className="flex items-start gap-3">
                                        <span className="mt-0.5 text-primary flex-shrink-0" aria-hidden>{t.icon}</span>
                                        <div>
                                            <p className="font-bold text-sm md:text-base dark:text-white text-gray-900">{t.title}</p>
                                            <p className="text-sm text-gray-600 dark:text-gray-400">{t.desc}</p>
                                        </div>
                                    </div>
                                )}
                            />
                        </SlideCard>
                    </div>
                </section>

                {/* ── 6 · Tres caminos / CTA final (tarjeta izquierda) ───────── */}
                <section id="section-6" className={SECTION_LEFT}>
                    <div className={`${PADDING} justify-center md:justify-start`}>
                        <SlideCard direction="left" className="glassCard rounded-2xl shadow-xl w-full md:w-[60%] lg:w-[55%] max-h-[88vh] overflow-y-auto p-5 md:p-8 flex flex-col gap-5">
                            <div>
                                <Eyebrow>Empieza hoy</Eyebrow>
                                <h2 className="text-3xl md:text-4xl font-extrabold dark:text-white text-gray-900">
                                    ¿Por dónde empiezas?
                                </h2>
                            </div>
                            <StaggerList
                                items={paths}
                                className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4"
                                renderItem={(c) => (
                                    <Link
                                        to={c.to}
                                        className="glassCard rounded-xl p-4 flex flex-col gap-2 group h-full
                                            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary
                                            hover:scale-[1.02] transition-transform duration-200"
                                    >
                                        <div className="flex items-center gap-2">
                                            <span className="text-primary group-hover:scale-110 transition-transform duration-200 flex-shrink-0" aria-hidden>
                                                {c.icon}
                                            </span>
                                            <p className="font-bold text-sm md:text-base dark:text-white text-gray-900">{c.title}</p>
                                        </div>
                                        <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400 flex-1">{c.desc}</p>
                                        <span className="btn btn-primary btn-xs md:btn-sm mt-1 self-start">{c.btn}</span>
                                    </Link>
                                )}
                            />
                        </SlideCard>
                    </div>
                </section>

                {/* ── 7 · Explora el diseño en 3D (pantalla completa) ────────── */}
                <section
                    id="section-7"
                    className="flex flex-col items-center justify-center w-full h-full overflow-hidden"
                    aria-label="Explora un diseño en 3D"
                >
                    <div className="w-full h-full flex flex-col items-center justify-center px-4 md:px-12 py-4 gap-4 md:gap-5">
                        <FadeIn className="text-center">
                            <Eyebrow tone="secondary">En 3D</Eyebrow>
                            <h2 className="text-2xl md:text-4xl font-extrabold dark:text-white text-gray-900">
                                Explora el resultado
                            </h2>
                        </FadeIn>
                        <div className="w-full max-w-3xl">
                            <HomeModel />
                        </div>
                        <FadeIn>
                            <BrandButton as="link" to="/customizers" size="md">
                                Diseña el tuyo <ArrowRight size={16} />
                            </BrandButton>
                        </FadeIn>
                    </div>
                </section>
            </main>
        </MotionConfig>
    );
}

export default HomePage;
